import { useRef, useState, type KeyboardEvent } from "react";
import { cx } from "./ui";

/**
 * The console's charts.
 *
 * Three deliberate borrowings from the customer dashboard's chart design, which
 * is documented in `UsageChart.tsx` there:
 *
 *   * **One accent, no categorical palette.** Series are drawn in the accent
 *     and neutrals, never in `mint`/`warn`/`danger` — those are documented as
 *     state, and a chart whose lines borrow them assigns meaning it does not
 *     have. `danger` appears only where a day is genuinely in a failed state.
 *   * **Composition is named, not coloured.** A stacked chart needs a colour
 *     per kind, and this design system deliberately has none. The readout under
 *     the chart names what a day was made of instead.
 *   * **A zero day stays a hairline**, so a quiet stretch reads as a span of
 *     time rather than a gap in the axis.
 */

/* -------------------------------------------------------------- range picker */

/**
 * The day-range picker, shared by every reporting surface.
 *
 * The ranges are served by the API rather than duplicated here, so a console
 * that hardcoded a list could not offer a range the API rejects.
 */
/**
 * The same control, for anything that is not a date range.
 *
 * Extracted from `RangePicker` rather than styled again beside it: two segmented
 * controls on one page that differ by a pixel of padding read as a bug, and the
 * date range and the grouping switch now sit in the same header on the AI usage
 * page. `RangePicker` keeps its own signature because `${n}d` is a label rule
 * rather than a caller's string.
 */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <div className="inline-flex rounded-md border border-line p-0.5" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
          className={cx(SEGMENT_BUTTON, option.value === value ? SEGMENT_ON : SEGMENT_OFF)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/*
 * The hit area, shared by both segmented controls.
 *
 * `pointer-coarse:min-h-11` is the same 44px `BUTTON_SIZES` in ui.tsx applies,
 * and for the same reason: this console gets worked from a phone during an
 * incident. These were 24px, which made them the smallest tap targets on any
 * screen here — in a console where every ordinary button had already been sized
 * for a thumb by hand.
 *
 * The variant keys off the input device rather than the viewport, so a desktop
 * window dragged narrow keeps the density this was drawn at. `min-h` rather
 * than `h`, so a label that wraps grows the control instead of clipping itself.
 */
const SEGMENT_BUTTON =
  "rounded px-2.5 py-1 text-xs transition-colors duration-200 ease-smooth " +
  "pointer-coarse:min-h-11 pointer-coarse:px-3.5";
const SEGMENT_ON = "bg-raised text-fg";
const SEGMENT_OFF = "text-fg-subtle hover:text-fg";

export function RangePicker({
  ranges,
  value,
  onChange,
}: {
  ranges: number[];
  value: number;
  onChange: (days: number) => void;
}) {
  /*
   * Delegated rather than drawn again. The two were identical markup, and a
   * hit-area rule that lives in one of them is a hit-area rule the other loses
   * the next time either is touched. The number/string round trip is the whole
   * cost of sharing it, and it stays inside this function so callers keep
   * passing days as numbers.
   */
  return (
    <Segmented
      options={ranges.map((days) => ({ value: String(days), label: `${days}d` }))}
      value={String(value)}
      onChange={(next) => onChange(Number(next))}
      label="Date range"
    />
  );
}

/* -------------------------------------------------------------------- bars */

export interface BarPart {
  /** Named in the readout, e.g. "apps". */
  label: string;
  value: number;
}

export interface BarPoint {
  day: string;
  value: number;
  /**
   * What the day was made of, named in the readout rather than drawn — see the
   * file comment. Absent for a series with no composition to name.
   */
  parts?: BarPart[];
}

/**
 * A daily bar chart over whatever the caller is measuring.
 *
 * Bars are proportional to the window's maximum, and a zero day stays a
 * hairline so the whole window reads as a span rather than a scattering of
 * islands. A flat window renders flat rather than being replaced with an empty
 * message — nothing to plot and nothing happened are different facts, and only
 * the first one is absence.
 */
export function DailyBars({
  points,
  format,
  label,
  windowLabel,
  emptyLabel = "Nothing in this window",
}: {
  points: BarPoint[];
  /** Turns a value into what the tooltip and readout show. */
  format: (value: number) => string;
  /** Describes the series for a screen reader, e.g. "Daily revenue earned". */
  label: string;
  /** How many days the window covers, for the accessible label. */
  windowLabel: string;
  emptyLabel?: string;
}) {
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const max = points.reduce((m, point) => Math.max(m, point.value), 0);

  if (points.length === 0) {
    return <p className="py-8 text-center text-sm text-fg-subtle">{emptyLabel}</p>;
  }

  const flat = max === 0;
  const selected = points.find((point) => point.day === selectedDay) ?? points.at(-1);
  // Dense windows get a hairline gap rather than the default 2px, so a 90-day
  // chart on a phone does not shrink every bar to nothing between its gutters.
  const gap = points.length > 45 ? "gap-px" : "gap-[2px]";

  return (
    <div>
      <div className={cx("flex h-24 items-end", gap)} aria-label={`${label}, ${windowLabel}`}>
        {points.map((point) => {
          const readout = describeBar(point, format);
          return (
            <button
              key={point.day}
              type="button"
              title={readout}
              aria-label={readout}
              aria-pressed={selected?.day === point.day}
              onClick={() => setSelectedDay(point.day)}
              onFocus={() => setSelectedDay(point.day)}
              className="group relative flex h-full flex-1 items-end focus:outline-none"
            >
              <span
                className={cx(
                  "w-full rounded-t-[2px] transition-colors group-hover:bg-accent",
                  flat
                    ? selected?.day === point.day
                      ? "bg-accent/60"
                      : "bg-line-strong"
                    : selected?.day === point.day
                      ? "bg-accent"
                      : "bg-accent/70",
                )}
                style={{ height: flat ? "2%" : `${Math.max(2, (point.value / max) * 100)}%` }}
              />
            </button>
          );
        })}
      </div>
      <div className="mt-1.5 flex justify-between text-[0.6875rem] text-fg-subtle">
        <span>{dayLabel(points[0]?.day)}</span>
        <span>{dayLabel(points[points.length - 1]?.day)}</span>
      </div>
      {selected && (
        <p className="mt-3 text-xs text-fg-muted" aria-live="polite">
          {describeBar(selected, format)}
        </p>
      )}
    </div>
  );
}

/**
 * One day, as a line of text: the total, then whatever it was made of.
 *
 * Shared by the tooltip, the accessible label and the readout, so a screen
 * reader and a pointer get the same sentence. Zero parts are dropped — "apps
 * $0.00" in a breakdown is a row saying nothing.
 */
function describeBar(point: BarPoint, format: (value: number) => string): string {
  const parts = (point.parts ?? []).filter((part) => part.value > 0);
  const total = `${dayLabel(point.day)}: ${format(point.value)}`;
  if (parts.length < 2) return total;

  return `${total} — ${parts.map((part) => `${part.label} ${format(part.value)}`).join(", ")}`;
}

export function dayLabel(iso: string | undefined): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/* ------------------------------------------------------------------ shares */

export interface Share {
  label: string;
  value: number;
  /** What this slice is, when the number alone would not say. */
  note?: string;
}

/**
 * One total, broken into the parts that compose it.
 *
 * For a figure already shown elsewhere on the page, never for one that only
 * exists here — the whole point is to answer "what is that made of", so the
 * total is stated at the top and the parts are shown as shares of it rather
 * than as free-standing amounts a reader might add up a second time.
 *
 * Ranked by the same three neutral levels the line chart uses, assigned by
 * position rather than by size: the order is fixed by the caller, so a slice
 * does not change colour when it overtakes another and a reader who has learnt
 * "the accent one is apps" keeps being right. A share of nothing is drawn as an
 * empty track rather than omitted, because a product earning zero is a fact and
 * a missing row reads as a product that does not exist.
 */
export function ShareBars({
  shares,
  total,
  format,
  emptyLabel = "Nothing earned in this period",
}: {
  shares: Share[];
  total: number;
  format: (value: number) => string;
  emptyLabel?: string;
}) {
  const tones: NonNullable<LineSeries["tone"]>[] = ["primary", "muted", "subtle"];

  if (total <= 0) {
    return <p className="py-6 text-center text-sm text-fg-subtle">{emptyLabel}</p>;
  }

  return (
    <ul className="space-y-3">
      {shares.map((share, index) => {
        // Guarded rather than assumed: the parts sum to the total by
        // construction, but a zero total would divide by zero and a negative
        // one — an adjustment-heavy month — must not draw a bar backwards.
        const pct = total > 0 ? Math.max(0, (share.value / total) * 100) : 0;
        const tone = tones[index % tones.length] ?? "muted";

        return (
          <li key={share.label}>
            {/* The figure and its share never wrap or shrink; the label is what
                gives way on a narrow card, because a truncated word still reads
                and a truncated amount is a wrong number. */}
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate text-sm text-fg">{share.label}</span>
              <span className="flex shrink-0 items-baseline gap-2">
                <span className="text-sm font-medium tabular-nums">{format(share.value)}</span>
                <span className="w-10 text-right text-xs tabular-nums text-fg-subtle">
                  {pct >= 0.5 ? `${Math.round(pct)}%` : "<1%"}
                </span>
              </span>
            </div>
            <div
              className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-line"
              /* The bar is decoration: the figure and the share are both already
                 text above it, so a screen reader reading this row twice would
                 be worse than it reading it once. */
              aria-hidden="true"
            >
              <div
                className="h-full rounded-full"
                style={{ width: `${Math.min(100, pct)}%`, background: LINE_STYLES[tone] }}
              />
            </div>
            {share.note && <p className="mt-1 text-xs text-fg-subtle">{share.note}</p>}
          </li>
        );
      })}
    </ul>
  );
}

/* ------------------------------------------------------------------- lines */

export interface LineSeries {
  label: string;
  /** A value per day, aligned with `days`. Null is "unknown", drawn as a gap. */
  values: (number | null)[];
  /**
   * `primary` is the accent; the rest are neutrals. There is no categorical
   * scale — a chart comparing four entities reads three neutral levels, with a
   * dash pattern distinguishing a fourth rather than a colour the palette
   * deliberately does not have.
   */
  tone?: "primary" | "muted" | "subtle";
  /**
   * Draws the line dashed, for a second series sharing the same neutral. The
   * legend shows the same dash, so the two never have to be told apart by
   * shape alone in the chart and by colour alone in the key.
   */
  dashed?: boolean;
}

const LINE_STYLES: Record<NonNullable<LineSeries["tone"]>, string> = {
  primary: "var(--color-accent)",
  muted: "var(--color-fg-muted)",
  subtle: "var(--color-fg-subtle)",
};

/**
 * A daily multi-series line chart.
 *
 * Lines live in a percentage coordinate space so the chart follows its
 * container; strokes are non-scaling, so a stretched viewBox cannot turn them
 * into ribbons. Selection follows the pointer and the arrow keys — one focus
 * stop for the whole chart rather than one per day, since a line chart is read
 * as a shape, not as ninety targets.
 *
 * `flags[i]` marks day `i` as in a failed state — a day billed under cost, say.
 * Drawn as a `danger` tick under the axis rather than a tint on the line,
 * because the flag is about the day and not the series.
 */
export function DailyLines({
  days,
  series,
  format,
  label,
  windowLabel,
  flags,
  flagsLabel,
  flagsPhrase,
  emptyLabel = "Nothing in this window",
}: {
  days: string[];
  series: LineSeries[];
  /** Turns a value into what the tooltip and readout show. */
  format: (value: number) => string;
  /** Describes the chart for a screen reader, e.g. "Platform growth". */
  label: string;
  windowLabel: string;
  flags?: boolean[];
  /** What a flag means, as the legend's sentence — e.g. "billed under cost". */
  flagsLabel?: string;
  /** The compact form appended to a flagged day's readout, e.g. "billed under cost". */
  flagsPhrase?: string;
  emptyLabel?: string;
}) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  const max = series.reduce(
    (m, line) => line.values.reduce<number>((acc, value) => Math.max(acc, value ?? 0), m),
    0,
  );

  if (days.length === 0) {
    return <p className="py-8 text-center text-sm text-fg-subtle">{emptyLabel}</p>;
  }

  const flat = max === 0;
  const xOf = (index: number) => (days.length === 1 ? 50 : (index / (days.length - 1)) * 100);
  const yOf = (value: number | null) =>
    value === null ? null : flat ? 50 : 100 - Math.min(100, Math.max(2, (value / max) * 100));

  const selected =
    selectedIndex === null || selectedIndex < 0 || selectedIndex >= days.length
      ? days.length - 1
      : selectedIndex;

  const readout =
    days.length > 0
      ? [
          describeLine(
            days[selected] ?? "",
            series.map((line) => ({ label: line.label, value: line.values[selected] })),
            format,
          ),
          flags?.[selected] ? flagsPhrase ?? "flagged" : null,
        ]
          .filter(Boolean)
          .join(" · ")
      : "";

  function selectIndexAt(event: { clientX: number }, rect: DOMRect, count: number) {
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    return Math.round(ratio * (count - 1));
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      setSelectedIndex(Math.max(0, selected - 1));
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      setSelectedIndex(Math.min(days.length - 1, selected + 1));
    } else if (event.key === "Escape") {
      event.preventDefault();
      (event.target as HTMLElement).blur();
    }
  }

  return (
    <div>
      <div
        ref={boxRef}
        tabIndex={0}
        role="img"
        aria-label={`${label}, ${windowLabel}. Use the arrow keys to read day by day.`}
        onKeyDown={onKeyDown}
        onFocus={() => setSelectedIndex(selected)}
        onPointerMove={(event) => {
          const rect = boxRef.current?.getBoundingClientRect();
          if (!rect) return;
          setSelectedIndex(selectIndexAt(event, rect, days.length));
        }}
        /* A tap is a tap, not a hover: pointermove does not fire for it on a
           touch screen, so the same math runs on click. */
        onClick={(event) => {
          const rect = boxRef.current?.getBoundingClientRect();
          if (!rect) return;
          setSelectedIndex(selectIndexAt(event, rect, days.length));
        }}
        onPointerLeave={() => setSelectedIndex(null)}
        className="group relative h-40 cursor-crosshair rounded-md focus:outline-none"
      >
        {/* Gridlines: quarters of the value axis, in `line`. */}
        <svg
          className="absolute inset-0 h-full w-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {[25, 50, 75].map((y) => (
            <line
              key={y}
              x1={0}
              x2={100}
              y1={y}
              y2={y}
              stroke="var(--color-line)"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {series.map((line) => {
            const points = line.values
              .map((value, index) => ({ x: xOf(index), y: yOf(value) }))
              .filter((point): point is { x: number; y: number } => point.y !== null);

            if (points.length === 0) return null;
            const path = points
              .map((point, index) => `${index === 0 ? "M" : "L"}${point.x},${point.y}`)
              .join(" ");

            return (
              <path
                key={line.label}
                d={path}
                fill="none"
                stroke={LINE_STYLES[line.tone ?? "muted"]}
                strokeDasharray={line.dashed ? "3 3" : undefined}
                strokeWidth={1.5}
                strokeLinecap="square"
                strokeLinejoin="miter"
                vectorEffect="non-scaling-stroke"
              />
            );
          })}
          {/* The selection rule */}
          <line
            x1={xOf(selected)}
            x2={xOf(selected)}
            y1={0}
            y2={100}
            stroke="var(--color-line-strong)"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        {/* A marker on each selected value, so the readout below can be tied to
            its point on the line. HTML rather than SVG: an SVG circle would
            stretch with the coordinate space. */}
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          {series.map((line) => {
            const value = line.values[selected];
            if (value === null || value === undefined) return null;
            return (
              <span
                key={line.label}
                className="absolute size-2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-surface"
                style={{
                  left: `${xOf(selected)}%`,
                  top: `${yOf(value)}%`,
                  background: LINE_STYLES[line.tone ?? "muted"],
                }}
              />
            );
          })}
        </div>

        {/* Danger ticks, under the axis rather than on the lines. */}
        {flags && (
          <div className="absolute inset-x-0 bottom-0 flex items-end" aria-hidden="true">
            {flags.map((flagged, index) =>
              flagged ? (
                <span
                  key={days[index]}
                  className="size-[3px] shrink-0 rounded-full bg-danger"
                  style={{
                    position: "absolute",
                    left: `${xOf(index)}%`,
                    bottom: 0,
                    transform: "translateX(-50%)",
                  }}
                />
              ) : null,
            )}
          </div>
        )}
      </div>

      <div className="mt-1.5 flex justify-between text-[0.6875rem] text-fg-subtle">
        <span>{dayLabel(days[0])}</span>
        <span>{dayLabel(days[days.length - 1])}</span>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
        {series.map((line) => (
          <span key={line.label} className="flex items-center gap-1.5 text-xs text-fg-muted">
            {/* The legend draws the line as it is drawn, dash and all, so the
                key and the chart cannot disagree about which series is which. */}
            <span
              className="h-0.5 w-3.5 shrink-0"
              style={{
                backgroundImage: line.dashed
                  ? `repeating-linear-gradient(90deg, ${LINE_STYLES[line.tone ?? "muted"]} 0 3px, transparent 3px 5px)`
                  : LINE_STYLES[line.tone ?? "muted"],
              }}
            />
            {line.label}
          </span>
        ))}
      </div>

      {flags?.some(Boolean) && (
        <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-fg-subtle">
          <span className="size-[3px] rounded-full bg-danger" aria-hidden="true" />
          {flagsLabel ?? "A red tick marks a day in a failed state."}
        </p>
      )}

      <p className="mt-2 text-xs text-fg-muted" aria-live="polite">
        {readout}
      </p>
    </div>
  );
}

/** One day of a line chart, as a sentence listing every series. */
function describeLine(
  day: string,
  points: { label: string; value: number | null | undefined }[],
  format: (value: number) => string,
): string {
  const named = points
    .filter((point) => point.value !== null && point.value !== undefined)
    .map((point) => `${point.label} ${format(point.value ?? 0)}`);
  return named.length === 0 ? dayLabel(day) : `${dayLabel(day)}: ${named.join(", ")}`;
}